import React from "react";
import intl from "react-intl-universal";
import classNames from "classnames";

import {UserId} from "../../utils/types";
import {Quiz, QuizStatsSum} from "../../@types/Quiz";
import {Quiz as QuizServer} from '../quiz/quiz-api';
import {FilterSchema} from "./DataSource";
import {ColId, ColSchema} from "../../ui/components/Table";
import {TableDataSource} from "./TableDataSource";

import {listQuizByState} from "../quiz/requests/listQuizByState";
import {FullDataSourceDeps, WithAppStateDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {HelpIcon} from "../../ui/components/icons/HelpIcon";
import {quizServerToQuizMSTSnapshotIn} from "../convertors/quizServerToQuizMSTSnapshotIn";
import {getQuizStatusTranslated} from "../../ui/screens/Quizzes/common/getQuizStatusTranslated";
import {MoreVerticalIcon} from "../../ui/components/icons/MoreVerticalIcon";
import {openQuizContextMenu} from "../../ui/screens/Quizzes/openQuizContextMenu";
import {InTextLabel} from "../../ui/components/InTextLabel";
import {Tooltip} from "../../ui/components/Tooltip";
import {highlightText} from "../../utils/highlightText";

import {
    getQuizStatUserKey,
    getUserResultFromQuiz,
    getUsersCompletedCount,
    parseQuizStatsSummary
} from "../../ui/screens/Quizzes/common/parseQuizStatsSummary";

import {screenSizeProvider} from "../ScreenSizeProvider";
import {onCreateQuiz, onDeleteQuiz, onUpdateQuiz} from "../../graphql/quiz/subscriptions";
import {QuizModel} from "../quiz/QuizModel";
import {createQuizModelEnv} from "../quiz/createQuizModelWithEnv";
import {
    QUIZ_RESULT_FILTER_VALUES
} from "../../ui/screens/Quizzes/common/table-filters/QuizResultFilter";

const colOrder: ColId[] = [
    'name',
    'questionsCount',
    'usersCompletedCount',
    'status',
    'publication_state',
    'actions',
]

const colOrderMobile: ColId[] = [
    'name',
    'usersCompletedCount',
    'status',
    'publication_state',
    'actions',
]

const colSchema: ColSchema = {
    name: {
        getTitle: () => intl.get('tests.tableCols.name').d('Test name'),
        alignLeft: true,
        type: 'string',
        minWidth: 120,
        maxWidth: 470,
    },

    questionsCount: {
        getTitle: () => intl.get('tests.tableCols.questions').d('Questions'),
        minWidth: 80,
        maxWidth: 234,
    },

    usersCompletedCount: {
        getTitle: () => intl.get('tests.tableCols.usersCompleted').d('Users completed'),
        minWidth: 80,
        maxWidth: 234,
    },

    publication_state: {
        getTitle: () => intl.get('tests.tableCols.state').d('Publication status'),
        minWidth: 120,
        maxWidth: 234,
    },

    status: {
        getTitle: () => intl.get('tests.tableCols.status').d('Results'),
        minWidth: 80,
        maxWidth: 234,
        icon: <Tooltip
            title={intl.get('tooltip.status.icon').d("Enter a user's name in the filter above to display their test results here.")}>
            <HelpIcon size={16}/>
        </Tooltip>
    },
    actions: {
        getTitle: () => '',
        minWidth: 56,
        maxWidth: 56
    },
}

const colSchemaMobile: ColSchema = {
    name: {
        getTitle: () => intl.get('tests.tableCols.name').d('Test name'),
        alignLeft: true,
        type: 'string',
        maxWidth: 320,
    },
    usersCompletedCount: {
        getTitle: () => intl.get('tests.tableCols.usersCompleted').d('Users completed'),
        minWidth: 153,
    },

    publication_state: {
        getTitle: () => "",
        minWidth: 48,
        maxWidth: 48,
    },

    status: {
        getTitle: () => intl.get('tests.tableCols.status').d('Results'),
        minWidth: 164,
        maxWidth: 234,
    },
    actions: {
        getTitle: () => '',
        minWidth: 56,
        maxWidth: 56
    },
}

export class QuizzesDataSource extends TableDataSource<QuizServer, Quiz, WithAppStateDataSourceDeps> {
    readonly _modelName = 'Quiz' as const;
    readonly _subscriptionGQLs = {
        onCreate: onCreateQuiz,
        onUpdate: onUpdateQuiz,
        onDelete: onDeleteQuiz,
    } as const
    wsId: string;

    constructor(deps: WithAppStateDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        this.deps.userState.dsManager!.getWsUsersDataSource(this.wsId).initialLoad().then(() => {
            this.setIsInitialized(true);
        })
    }

    private buildQuizModelEnv() {
        return createQuizModelEnv({
            dataClientService: this.deps.dataClientService,
            authService: this.deps.authService,
            getCurrentUserId: () => this.deps.userState.currentUserId,
        });
    }

    get syncHash() {
        return this.wsId.slice(0,6);
    }

    getFilterDefaultValues(): {} {
        const _isNotAdmin = !this.deps.userState.isCurrentUserAdmin;
        if (_isNotAdmin) {
            return {
                name: '',
                publication_state: 'published',
                userId: this.deps.userState.currentUserId
            };
        }
        return {
            name: '',
            publication_state: null,
            userId: null,
            status: null
        }
    }

    getFilterSchema(): FilterSchema<Quiz> {
        const _isNotAdmin = !this.deps.userState.isCurrentUserAdmin;
        return {
            name: {
                type: 'client',
                apply: (quiz: Quiz) => quiz.name.toLowerCase().indexOf(this.filterValues.name.toLowerCase()) !== -1,
            },
            publication_state: {
                type: _isNotAdmin ? 'server' : 'client',
                apply: (quiz: Quiz) => this.filterValues.publication_state ? quiz.publication_state === this.filterValues.publication_state : true,
            },
            userId: {
                type: 'client',
                apply: () => true, /* doesn't actually filter but affect data in the rows */
            },
            status: {
                type: 'client',
                apply: (quiz: Quiz) => {
                    if (!this.filterValues.userId) {
                        return false;
                    }
                    const userResult = getUserResultFromQuiz(quiz, this.filterValues.userId);
                    switch (this.filterValues.status) {
                        case QUIZ_RESULT_FILTER_VALUES.NOT_STARTED:
                            return userResult === undefined;
                        case QUIZ_RESULT_FILTER_VALUES.PASSED:
                            return userResult?.status === 'passed';
                        case QUIZ_RESULT_FILTER_VALUES.FAILED:
                            return userResult?.status === 'failed';
                        default:
                            return true;
                    }
                }
            }
        }
    }

    loadGQLFetch(filterValues: Record<ColId, any>) {
        let limit = undefined;
        if (import.meta.env.VITE_APP_ENV !== 'production') {
            limit = 20;
        }
        return listQuizByState(
            this.deps.dataClientService,
            this.deps.authService,
            this.wsId, 
            filterValues.publication_state, 
            undefined, 
            limit
        );
    };

    loadMoreGQLFetch(nextToken: string, filterValues: Record<ColId, any>) {
        let limit = undefined;
        if (import.meta.env.VITE_APP_ENV !== 'production') {
            limit = 20;
        }
        return listQuizByState(
            this.deps.dataClientService,
            this.deps.authService,
            this.wsId, 
            filterValues.publication_state, 
            nextToken, 
            limit
        );
    };

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey('pgServer');
    };

    getColOrder() {
        const _isReadOnly = this.deps.userState.isCurrentUserReadOnly;
        const isMobile = screenSizeProvider.view !== 'desktop';

        let actualCols = isMobile ? colOrderMobile.slice() : colOrder.slice();
        if (_isReadOnly) {
            actualCols = actualCols.filter(colId => colId !== 'publication_state');
        }
        return actualCols;
    }

    getColsSchema() {
        const isMobile = screenSizeProvider.view !== 'desktop';
        return isMobile ? colSchemaMobile : colSchema;
    }

    mapServerResponseToLocalRow(row: QuizServer): Quiz {
        return ({
            ...row,
            questionsOrder: JSON.parse(row.questionsOrder!),
            stats: row.stats ? JSON.parse(row.stats) : {},
            meta: row.meta ? JSON.parse(row.meta) : {},
        }) as Quiz
    }

    mapResultToTableRow = (quiz: Quiz) => {
        const quizStats: Record<UserId, QuizStatsSum> | null = parseQuizStatsSummary(quiz);
        const usersCompletedCount = getUsersCompletedCount(quizStats);
        const isMobile = screenSizeProvider.view !== 'desktop';

        const statsKey = this.filterValues.userId ? getQuizStatUserKey(this.filterValues.userId) : null;

        const publishedText = intl.get('quiz.published').d('Published');
        const draftText = intl.get('quiz.draft').d('Draft');

        return {
            ...quiz,
            name: quiz.name !== ""
                ? <span dangerouslySetInnerHTML={{'__html': highlightText(this.filterValues.name, quiz.name)}}/>
                : intl.get('quizzes.untitledQuiz').d("Untitled test"),
            publication_state: quiz.publication_state === 'published' ? (
                <InTextLabel className="body-large" type={'success'}
                             text={!isMobile ? publishedText : publishedText.charAt(0)}/>
            ) : (
                <InTextLabel className="body-large" type={'neutral'}
                             text={!isMobile ? draftText : draftText.charAt(0)}/>
            ),
            usersCompletedCount: `${usersCompletedCount} / ${this.deps.userState.dsManager!.getWsUsersDataSource(this.wsId).allRows.length}`,
            status: quizStats && statsKey && quizStats[statsKey] ? (
                <span className={classNames({
                    'Quizzes_td--passed': quizStats[statsKey].status === 'passed',
                    'Quizzes_td--failed': quizStats[statsKey].status === 'failed',
                })}>
                    {getQuizStatusTranslated(quizStats[statsKey].status)}
                </span>
            ) : null,
            actions: (
                <div className="QuizzesMoreActions"
                     onClick={(event) => {
                         const targetDom = event.currentTarget;
                         event.preventDefault();
                         event.stopPropagation();

                         if (this.deps.appState.contextMenu?.type === 'quiz') {
                             this.deps.appState.setContextMenu(null);
                         } else {
                             openQuizContextMenu(
                                 this.deps.appState,
                                 QuizModel.create(
                                     quizServerToQuizMSTSnapshotIn(JSON.parse(JSON.stringify(quiz))), // required because rows are already observable
                                     this.buildQuizModelEnv()
                                 )
                                 , {
                                     getTargetBoundingBox: () => targetDom.getBoundingClientRect(),
                                 }, {
                                     onQuizzesPage: true,
                                     onSuccessCallBack: () => {
                                     },
                                 }
                             );
                         }
                     }}>
                    <Tooltip title={intl.get('tooltip.quizzes.more').d("More")} isInBox={false}>
                        <MoreVerticalIcon/>
                    </Tooltip>
                </div>
            ),
            raw: quiz,
        }
    };
}
